/**
 * Single source of truth for the SalesTrak analytics taxonomy, shared across
 * the workspace. `AnalyticsContext` and `AnalyticsScreenName` are TS string
 * enums — their runtime values ARE the wire strings sent to the analytics API,
 * so a typo'd context or screen name can't silently fragment the dataset.
 *
 * The event catalog stays a literal map (not an enum) on purpose: it powers the
 * discriminated `TrackEvent` union below, which narrows `event` to a context's
 * allowed events at every call site. Add new contexts/events/screens here first.
 */

export const AnalyticsContext = {
  Auth: "auth",
  Automation: "automation",
  Issue: "issue",
  Kanban: "kanban",
  Navigation: "navigation",
  Note: "note",
  Search: "search",
  Settings: "settings",
  Team: "team",
  WidgetView: "widget_view",
  Workspace: "workspace",
} as const;
export type AnalyticsContext = (typeof AnalyticsContext)[keyof typeof AnalyticsContext];

/**
 * Named event constants — one per `event` string, so call sites reference
 * `IssueEvent.ChangeGrouping` instead of a bare `"change_grouping"`. These are
 * `as const` objects (not TS `enum`s) on purpose: their member types ARE the
 * string-literal types, so they stay assignable to the `TrackEvent` union built
 * from `ANALYTICS_EVENTS` below — a string `enum` member is nominal and would
 * not. `ANALYTICS_EVENTS` references these members, so the catalog and the
 * constants can never drift.
 */
export const IssueEvent = {
  View: "view",
  Edit: "edit",
  CreateStart: "create_start",
  CreateFinish: "create_finish",
  UpdateAge: "update_age",
  ChangeGrouping: "change_grouping",
  Search: "search",
  ApplyFilter: "apply_filter",
  ToggleOnlyMe: "toggle_only_me",
  SwitchTab: "switch_tab",
  BulkChangeStage: "bulk_change_stage",
  BulkUpdateAssignees: "bulk_update_assignees",
} as const;

// Automation lifecycle. `Create` fires once an automation is actually
// persisted (scratch, duplicate, or from a Browse template). Its `data`
// carries `source` ("scratch" | "duplicate" | "template"), `template` (the
// template key, or null), and `scope` ("workspace" | "workflow").
export const AutomationEvent = {
  Create: "create",
} as const;

export const KanbanEvent = {
  Rename: "rename",
  Archive: "archive",
  Unarchive: "unarchive",
  Delete: "delete",
  ChangeStage: "change_stage",
  Favorite: "favorite",
  Unfavorite: "unfavorite",
  MoveWorkflow: "move_workflow",
  UpdateOwners: "update_owners",
  UpdateAssignees: "update_assignees",
  DragMoveStage: "drag_move_stage",
  DragReorder: "drag_reorder",
} as const;

export const TeamEvent = {
  Open: "open",
  Leave: "leave",
  Search: "search",
} as const;

export const WidgetViewEvent = {
  AddWidget: "add_widget",
  ConfigureWidget: "configure_widget",
  DeleteWidget: "delete_widget",
  DuplicateWidget: "duplicate_widget",
  ReplaceWidget: "replace_widget",
  Rearrange: "rearrange",
  FilterDate: "filter_date",
  FilterMembers: "filter_members",
  ToggleOnlyMe: "toggle_only_me",
  // Ownership model: the personal fork of a shared view…
  Personalize: "personalize",
  ResetPersonalLayout: "reset_personal_layout",
  // …the editor's Personal ↔ Team-template lens switch and the "Save for
  // just me" destination for staged edits…
  SwitchViewLens: "switch_view_lens",
  SaveAsPersonal: "save_as_personal",
  // …and the editor staging bar on shared views.
  SaveForTeam: "save_for_team",
  DiscardStagedChanges: "discard_staged_changes",
  // The explicit board edit mode (toolbar Edit ↔ Done).
  EnterEditMode: "enter_edit_mode",
  ExitEditMode: "exit_edit_mode",
  // The Start-over dialog: replace the whole board with a template / blank.
  ApplyTemplate: "apply_template",
  ClearBoard: "clear_board",
} as const;

export const AuthEvent = {
  SignIn: "sign_in",
  SignUp: "sign_up",
  SignOut: "sign_out",
} as const;

export const NavigationEvent = {
  ScreenView: "screen_view",
} as const;

export const NoteEvent = {
  Create: "create",
  Update: "update",
  Delete: "delete",
  React: "react",
} as const;

export const SearchEvent = {
  Command: "command",
  SelectResult: "select_result",
} as const;

export const SettingsEvent = {
  Create: "create",
  Update: "update",
  Delete: "delete",
  Archive: "archive",
  Unarchive: "unarchive",
  Revoke: "revoke",
  Reorder: "reorder",
  SetDefault: "set_default",
  Duplicate: "duplicate",
  AddMembers: "add_members",
  RemoveMembers: "remove_members",
  UpdateMembership: "update_membership",
  UpdateRoles: "update_roles",
  UpdateTeams: "update_teams",
  ResendInvite: "resend_invite",
  CancelInvite: "cancel_invite",
  Invite: "invite",
} as const;

export const WorkspaceEvent = {
  Switch: "switch",
} as const;

/**
 * Named constants for the `data` payload VALUES that recur across call sites,
 * so the dataset can't fragment on a typo'd `"borad"` or `"set-primary"`.
 * (`data` itself stays an open `Record`; these just standardize the values we
 * reuse.) `AnalyticsScope` mirrors `IssueScope["kind"]`.
 */
export const AnalyticsView = {
  Board: "board",
  List: "list",
  Member: "member",
  WidgetView: "widget_view",
} as const;

export const AnalyticsScope = {
  Workflow: "workflow",
  Team: "team",
  Member: "member",
} as const;

export const BulkOperation = {
  Add: "add",
  Remove: "remove",
  SetPrimary: "set_primary",
} as const;

export const BulkMode = {
  Where: "where",
  Ids: "ids",
} as const;

export type WidgetViewEvent = (typeof WidgetViewEvent)[keyof typeof WidgetViewEvent];
export type AnalyticsView = (typeof AnalyticsView)[keyof typeof AnalyticsView];
export type AnalyticsScope = (typeof AnalyticsScope)[keyof typeof AnalyticsScope];
export type BulkOperation = (typeof BulkOperation)[keyof typeof BulkOperation];
export type BulkMode = (typeof BulkMode)[keyof typeof BulkMode];

export const ANALYTICS_EVENTS = {
  [AnalyticsContext.Auth]: [AuthEvent.SignIn, AuthEvent.SignUp, AuthEvent.SignOut],
  [AnalyticsContext.Automation]: [AutomationEvent.Create],
  // `view`/`edit`/`create_*`/`update_age` are per-issue; the rest are view-level
  // interactions shared by the board, list, and member views (grouping,
  // search, filtering, Only-Me, board/list/dashboard tab switching, bulk ops).
  [AnalyticsContext.Issue]: [
    IssueEvent.View,
    IssueEvent.Edit,
    IssueEvent.CreateStart,
    IssueEvent.CreateFinish,
    IssueEvent.UpdateAge,
    IssueEvent.ChangeGrouping,
    IssueEvent.Search,
    IssueEvent.ApplyFilter,
    IssueEvent.ToggleOnlyMe,
    IssueEvent.SwitchTab,
    IssueEvent.BulkChangeStage,
    IssueEvent.BulkUpdateAssignees,
  ],
  [AnalyticsContext.Kanban]: [
    KanbanEvent.Rename,
    KanbanEvent.Archive,
    KanbanEvent.Unarchive,
    KanbanEvent.Delete,
    KanbanEvent.ChangeStage,
    KanbanEvent.Favorite,
    KanbanEvent.Unfavorite,
    KanbanEvent.MoveWorkflow,
    KanbanEvent.UpdateOwners,
    KanbanEvent.UpdateAssignees,
    KanbanEvent.DragMoveStage,
    KanbanEvent.DragReorder,
  ],
  [AnalyticsContext.Navigation]: [NavigationEvent.ScreenView],
  [AnalyticsContext.Note]: [
    NoteEvent.Create,
    NoteEvent.Update,
    NoteEvent.Delete,
    NoteEvent.React,
  ],
  [AnalyticsContext.Search]: [SearchEvent.Command, SearchEvent.SelectResult],
  [AnalyticsContext.Settings]: [
    SettingsEvent.Create,
    SettingsEvent.Update,
    SettingsEvent.Delete,
    SettingsEvent.Archive,
    SettingsEvent.Unarchive,
    SettingsEvent.Revoke,
    SettingsEvent.Reorder,
    SettingsEvent.SetDefault,
    SettingsEvent.Duplicate,
    SettingsEvent.AddMembers,
    SettingsEvent.RemoveMembers,
    SettingsEvent.UpdateMembership,
    SettingsEvent.UpdateRoles,
    SettingsEvent.UpdateTeams,
    SettingsEvent.ResendInvite,
    SettingsEvent.CancelInvite,
    SettingsEvent.Invite,
  ],
  // Teams-list page: opening a team from the list, leaving a team, searching.
  [AnalyticsContext.Team]: [TeamEvent.Open, TeamEvent.Leave, TeamEvent.Search],
  // The `WidgetView` host (my-work, workflow/team Dashboard tabs): widget
  // lifecycle + the view's date/member/Only-Me filter toolbar.
  [AnalyticsContext.WidgetView]: [
    WidgetViewEvent.AddWidget,
    WidgetViewEvent.ConfigureWidget,
    WidgetViewEvent.DeleteWidget,
    WidgetViewEvent.DuplicateWidget,
    WidgetViewEvent.ReplaceWidget,
    WidgetViewEvent.Rearrange,
    WidgetViewEvent.FilterDate,
    WidgetViewEvent.FilterMembers,
    WidgetViewEvent.ToggleOnlyMe,
    WidgetViewEvent.Personalize,
    WidgetViewEvent.ResetPersonalLayout,
    WidgetViewEvent.SwitchViewLens,
    WidgetViewEvent.SaveAsPersonal,
    WidgetViewEvent.SaveForTeam,
    WidgetViewEvent.DiscardStagedChanges,
    WidgetViewEvent.EnterEditMode,
    WidgetViewEvent.ExitEditMode,
    WidgetViewEvent.ApplyTemplate,
    WidgetViewEvent.ClearBoard,
  ],
  [AnalyticsContext.Workspace]: [WorkspaceEvent.Switch],
} as const satisfies Record<AnalyticsContext, readonly string[]>;

type AnalyticsCatalog = typeof ANALYTICS_EVENTS;

/**
 * Discriminated on `context`, so `event` narrows to that context's events at
 * every call site.
 */
export type TrackEvent = {
  [C in AnalyticsContext]: {
    context: C;
    event: AnalyticsCatalog[C][number];
    label?: string;
    data?: Record<string, unknown>;
  };
}[AnalyticsContext];

/**
 * Migration-window continuity for the taxonomy rename (card→widget event
 * names, `surface`→`view` payload key). The NEW names stay canonical; these
 * maps are the single source of truth for the legacy wire names, consumed by
 * the client's central emit path (`AnalyticsClient.track` →
 * `expandLegacyAliases`) so downstream dashboards keyed on the old names keep
 * matching events until they migrate. Delete this whole block (and the
 * `expandLegacyAliases` call in the client) once downstream consumers have
 * moved to the canonical names.
 */

/** Renamed widget lifecycle events → their retired `*_card` wire names. */
export const LEGACY_EVENT_ALIASES = {
  [WidgetViewEvent.AddWidget]: "add_card",
  [WidgetViewEvent.ConfigureWidget]: "configure_card",
  [WidgetViewEvent.DeleteWidget]: "delete_card",
  [WidgetViewEvent.DuplicateWidget]: "duplicate_card",
} as const satisfies Partial<Record<WidgetViewEvent, string>>;

/** Renamed `data` payload keys → their retired twins, dual-written on every event. */
export const LEGACY_DATA_KEY_ALIASES = {
  view: "surface",
} as const satisfies Record<string, string>;

/**
 * Marker field stamped onto mirrored legacy-named events so downstream can
 * split (or drop) the duplicates without double counting.
 */
export const LEGACY_ALIAS_MARKER = "deprecated_alias";

export type LegacyEventAlias = (typeof LEGACY_EVENT_ALIASES)[keyof typeof LEGACY_EVENT_ALIASES];

/**
 * A mirrored event emitted under a retired event name. Same shape as
 * `TrackEvent`, but its `event` is a legacy wire name outside the canonical
 * catalog and its `data` carries `deprecated_alias: true`.
 */
export type LegacyAliasEvent = {
  context: typeof AnalyticsContext.WidgetView;
  event: LegacyEventAlias;
  label?: string;
  data?: Record<string, unknown>;
};

/** Dual-write renamed payload keys: `view` also goes out as legacy `surface`. */
function withLegacyDataKeys(data: Record<string, unknown>): Record<string, unknown> {
  let out = data;
  for (const [canonical, legacy] of Object.entries(LEGACY_DATA_KEY_ALIASES)) {
    if (canonical in out && !(legacy in out)) {
      out = { ...out, [legacy]: out[canonical] };
    }
  }
  return out;
}

/**
 * Expand one canonical event into the wire events to emit during the
 * migration window: the canonical event (with legacy payload keys
 * dual-written into `data`) first, then — for the renamed widget lifecycle
 * events — a mirror under the retired `*_card` name with the same payload
 * plus the `deprecated_alias: true` marker.
 */
export function expandLegacyAliases(event: TrackEvent): Array<TrackEvent | LegacyAliasEvent> {
  // An event with no `data` keeps the key absent — an explicit `undefined`
  // reads as a payload on the wire.
  const canonical: TrackEvent =
    event.data === undefined ? event : { ...event, data: withLegacyDataKeys(event.data) };
  const out: Array<TrackEvent | LegacyAliasEvent> = [canonical];
  if (canonical.context === AnalyticsContext.WidgetView) {
    const legacyName = (LEGACY_EVENT_ALIASES as Partial<Record<string, LegacyEventAlias>>)[
      canonical.event
    ];
    if (legacyName) {
      out.push({
        ...canonical,
        event: legacyName,
        data: { ...(canonical.data ?? {}), [LEGACY_ALIAS_MARKER]: true },
      });
    }
  }
  return out;
}

/**
 * Curated labels for navigation/screen_view events. Deliberately hand-named
 * (not derived from route paths) so labels stay stable across route refactors
 * and never leak entity ids.
 */
export const AnalyticsScreenName = {
  Dashboard: "dashboard",
  Inbox: "inbox",
  Map: "map",
  MemberDetail: "member_detail",
  Members: "members",
  MyWork: "my_work",
  Teams: "teams",
  TeamBoard: "team_board",
  TeamList: "team_list",
  TeamDashboard: "team_dashboard",
  TeamOverview: "team_overview",
  TeamMembers: "team_members",
  Workflows: "workflows",
  WorkflowBoard: "workflow_board",
  WorkflowList: "workflow_list",
  WorkflowDashboard: "workflow_dashboard",
  SettingsAccess: "settings_access",
  SettingsAccessKeyCreate: "settings_access_key_create",
  SettingsAccessKeyDetail: "settings_access_key_detail",
  SettingsAccessKeyEdit: "settings_access_key_edit",
  SettingsApi: "settings_api",
  SettingsApiKeyCreate: "settings_api_key_create",
  SettingsApiKeyDetail: "settings_api_key_detail",
  SettingsApiKeyEdit: "settings_api_key_edit",
  SettingsCreateTeam: "settings_create_team",
  SettingsCustomFields: "settings_custom_fields",
  SettingsIssueSources: "settings_issue_sources",
  SettingsIssueTypeDetail: "settings_issue_type_detail",
  SettingsIssueTypes: "settings_issue_types",
  SettingsMemberDetail: "settings_member_detail",
  SettingsMembers: "settings_members",
  SettingsPermissions: "settings_permissions",
  SettingsPreferences: "settings_preferences",
  SettingsProfile: "settings_profile",
  SettingsReasons: "settings_reasons",
  SettingsStageDetail: "settings_stage_detail",
  SettingsTeamDetail: "settings_team_detail",
  SettingsTeamMembers: "settings_team_members",
  SettingsTeams: "settings_teams",
  SettingsTrash: "settings_trash",
  SettingsUpdates: "settings_updates",
  SettingsWebhookCreate: "settings_webhook_create",
  SettingsWebhookDetail: "settings_webhook_detail",
  SettingsWebhookEdit: "settings_webhook_edit",
  SettingsWorkflowDetail: "settings_workflow_detail",
  SettingsWorkflowStages: "settings_workflow_stages",
  SettingsWorkflows: "settings_workflows",
} as const;
export type AnalyticsScreenName = (typeof AnalyticsScreenName)[keyof typeof AnalyticsScreenName];
